<template>
  <div v-if="dialogType === 'borrowerTaskGrouped' || dialogType === 'internalTaskGrouped'" class="ccgd-groups">
    <div v-for="g in dataItems" :key="groupKey(g)" class="ccgd-group">
      <button class="ccgd-group-head" type="button" @click="toggleGroup(groupKey(g))">
        <div class="ccgd-group-left">
          <div class="ccgd-group-meta">
            <span class="ccgd-icon-wrap" aria-hidden="true">
              <i
                class="fa-duotone ccgd-meta-icon"
                :class="dialogType === 'internalTaskGrouped' ? 'fa-clipboard-check' : 'fa-file-contract'"
              ></i>
            </span>

            <div class="ccgd-meta-block">
              <div class="ccgd-meta-line">
                <span class="ccgd-meta-label">Loan</span>
                <span class="ccgd-meta-value">{{ g.loanNumber }}</span>
              </div>

              <div class="ccgd-meta-line">
                <span class="ccgd-meta-label">Borrower</span>
                <span class="ccgd-meta-value ccgd-borrower-text">
                  {{ (g.borrowers || []).join(", ") }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="ccgd-group-right">
          <div class="ccgd-count-wrap">
            <div class="ccgd-count">{{ (g.tasks || []).length }}</div>
            <div class="ccgd-count-label">Tasks</div>
          </div>
          <i class="fa-solid" :class="isCollapsed(groupKey(g)) ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </div>
      </button>

      <div v-show="!isCollapsed(groupKey(g))" class="ccgd-group-body">
        <div
          v-for="t in (g.tasks || [])"
          :key="t.id || t.task_id || (t.f364 + '_' + (t.created || '') + '_' + (t.title || ''))"
          class="ccgd-row-shell"
          :class="{ selected: !!t[selectedField] }"
          @click="$emit('row-click', { dataItem: t, event: $event })"
        >
          <div class="ccgd-list-row">
            <div class="ccgd-list-body" @click.stop>
              <BorrowerTaskCard
                v-if="dialogType === 'borrowerTaskGrouped'"
                :item="t"
                :selectedField="selectedField"
                :selectedIds="selectedIds"
                @toggle-selected="$emit('toggle-selected', t)"
                @row-click="$emit('row-click', { dataItem: t, event: $event })"
              />

              <InternalTaskCard
                v-else
                :item="t"
                @complete="$emit('complete-task', t)"
                @row-click="$emit('row-click', { dataItem: t, event: $event })"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ListView v-else :item="'lists'" :data-items="dataItems" :style="{ height: '100%' }">
    <template #lists="{ props: lp }">
      <div
        class="ccgd-row-shell"
        :class="{ selected: !!lp.dataItem[selectedField] }"
        @click="$emit('row-click', { dataItem: lp.dataItem, event: $event })"
      >
        <div class="ccgd-list-row">
          <div class="ccgd-list-body" @click.stop>
            <component
              :is="resolvedCard"
              :item="lp.dataItem"
              :selectedField="selectedField"
              :selectedIds="selectedIds"
              @toggle-selected="$emit('toggle-selected', lp.dataItem)"
              @row-click="$emit('row-click', { dataItem: lp.dataItem, event: $event })"
              v-on="resolvedCardEvents(lp.dataItem)"
            >
              <template #actionsCell>
                <slot name="actionsCell" v-bind="lp" />
              </template>
            </component>
          </div>
        </div>
      </div>
    </template>
  </ListView>
</template>

<script>
import { ListView } from "@progress/kendo-vue-listview"

import InternalTaskCard from "@/components/commandCenter/InternalTaskCard.vue"
import BorrowerTaskCard from "@/components/commandCenter/BorrowerTaskCard.vue"
import ApplicationCard from "@/components/commandCenter/ApplicationCard.vue"
import LoanPipelineCard from "@/components/commandCenter/LoanPipelineCard.vue"
import FallbackCard from "@/components/commandCenter/FallbackCard.vue"

const CARD_MAP = {
  borrowerTask: BorrowerTaskCard,
  internalTask: InternalTaskCard,
  application: ApplicationCard,
  loan: LoanPipelineCard
}

export default {
  name: "GridDialogList",
  components: { ListView, InternalTaskCard, BorrowerTaskCard, ApplicationCard, LoanPipelineCard, FallbackCard },
  props: {
    dataItems: { type: Array, default: () => [] },
    selectedField: { type: String, default: "selected" },
    dialogType: { type: String, default: "" },
    selectedIds: { type: Set, default: () => new Set() },
    autoExpandGroups: { type: Boolean, default: false },
    expandOnlyKeys: { type: Array, default: () => [] }
  },
  data() {
    return {
      collapsedLoans: new Set()
    }
  },
  computed: {
    resolvedCard() {
      return CARD_MAP[this.dialogType] || FallbackCard
    },
    isGrouped() {
      return this.dialogType === "borrowerTaskGrouped" || this.dialogType === "internalTaskGrouped"
    }
  },
  watch: {
    dialogType: {
      immediate: true,
      handler() {
        this._syncCollapsed()
      }
    },
    dataItems: {
      deep: false,
      handler() {
        this._syncCollapsed()
      }
    },
    autoExpandGroups() {
      this._syncCollapsed()
    },
    expandOnlyKeys() {
      this._syncCollapsed()
    }
  },
  methods: {
    groupKey(g) {
      const loan = String(g?.loanNumber || "")
      const borrowers = Array.isArray(g?.borrowers) ? g.borrowers.filter(Boolean).join("|") : ""
      return `${loan}__${borrowers}`
    },
    _syncCollapsed() {
      if (!this.isGrouped) return

      if (!Array.isArray(this.dataItems) || this.dataItems.length === 0) {
        this.collapsedLoans = new Set()
        return
      }

      if (this.autoExpandGroups) {
        const allow = new Set((this.expandOnlyKeys || []).map(k => String(k)).filter(Boolean))
        if (allow.size) {
          const collapsed = new Set()
          this.dataItems.forEach(g => {
            const k = String(this.groupKey(g))
            if (k && !allow.has(k)) collapsed.add(k)
          })
          this.collapsedLoans = collapsed
          return
        }

        this.collapsedLoans = new Set()
        return
      }

      const keys = this.dataItems.map(g => this.groupKey(g)).filter(Boolean)
      this.collapsedLoans = new Set(keys)
    },
    resolvedCardEvents(item) {
      if (this.dialogType === "internalTask") return { complete: () => this.$emit("complete-task", item) }
      if (this.dialogType === "application") return { "open-app": () => this.$emit("open-app", item) }
      return {}
    },
    isCollapsed(key) {
      return this.collapsedLoans.has(String(key))
    },
    toggleGroup(key) {
      const k = String(key)
      if (this.collapsedLoans.has(k)) this.collapsedLoans.delete(k)
      else this.collapsedLoans.add(k)

      this.collapsedLoans = new Set(this.collapsedLoans)
    }
  }
}
</script>

<style src="@/assets/css/commandcenter/commandcenter.css"></style>
<style scoped>
.ccgd-groups {
  height: 100%;
  overflow: auto;
  padding: 8px 10px;
}

.ccgd-group {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  margin-bottom: 10px;
}

.ccgd-group-head {
  width: 100%;
  border: none;
  background: rgba(15, 23, 42, 0.02);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  cursor: pointer;
  text-align: left;
}

.ccgd-group-meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ccgd-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.18);
  flex: 0 0 auto;
}

.ccgd-meta-icon {
  font-size: 17px;
  line-height: 1;
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(37, 99, 235, 0.28);
  --fa-secondary-opacity: 1;
}

.ccgd-meta-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ccgd-meta-line {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}

.ccgd-meta-label {
  font-size: 10.5px;
  font-weight: 950;
  color: rgba(37, 99, 235, 0.95);
  letter-spacing: 0.2px;
  text-transform: uppercase;
  flex: 0 0 auto;
}

.ccgd-meta-value {
  font-size: 13px;
  font-weight: 950;
  color: #0f172a;
  min-width: 0;
}

.ccgd-borrower-text {
  font-weight: 850;
  color: rgba(15, 23, 42, 0.78);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 620px;
}

.ccgd-group-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: rgba(15, 23, 42, 0.7);
  flex: 0 0 auto;
}

.ccgd-count-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1;
  gap: 2px;
}

.ccgd-count {
  font-weight: 950;
  font-size: 19px;
  color: rgba(37, 99, 235, 0.98);
}

.ccgd-count-label {
  font-size: 10.5px;
  font-weight: 950;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  color: rgba(37, 99, 235, 0.9);
}

.ccgd-group-body {
  padding: 8px 10px;
}

.cgsdark .ccgd-group {
  background: rgba(2, 6, 23, 0.55);
  border-color: rgba(148, 163, 184, 0.14);
}

.cgsdark .ccgd-group-head {
  background: rgba(148, 163, 184, 0.08);
}

.cgsdark .ccgd-meta-value {
  color: rgba(226, 232, 240, 0.92);
}

.cgsdark .ccgd-borrower-text {
  color: rgba(226, 232, 240, 0.78);
}

.cgsdark .ccgd-group-right {
  color: rgba(226, 232, 240, 0.72);
}

.cgsdark .ccgd-icon-wrap {
  background: rgba(59, 130, 246, 0.16);
  border-color: rgba(59, 130, 246, 0.22);
}

.cgsdark .ccgd-meta-label {
  color: rgba(96, 165, 250, 0.95);
}

.cgsdark .ccgd-count {
  color: rgba(96, 165, 250, 0.98);
}

.cgsdark .ccgd-count-label {
  color: rgba(96, 165, 250, 0.92);
}

.k-dialog {
  max-height: unset !important;
}

:deep(.k-dialog) {
  max-height: unset !important;
}
</style>